import { debounce } from '@/helpers/form'
import { type DocView } from '@/models/doc'
import type { DocContent, DocWithContent } from '@/models/interfaces'
import { Timer } from '@/helpers/timer'
import { emitter } from '@/services/mitt'
import { backend } from '@/services/backend'
import { updateDocsUserDataCache, updateUserDocsCache } from '@/helpers/cache'
import { assertNotNull } from '@/helpers/typing'
import { isFreePlan } from '@/services/auth'
import { Mixpanel } from '@/analytics/mixpanel'
import { isDocContentPage } from '@/helpers/router'
import { completionService } from '@/services/completion'
import { Sentry } from '@/services/sentry'

/**
 * Track access and read progress for the document.
 */

export class ReadingProgress {
  // The document to track the progress for
  private _doc: DocView

  private _tProgress: Timer
  private _tPositionRestore: Timer
  private _tPositionSave: Timer

  private _scrollHandler: (...args: any[]) => void

  /** The 20 second timer has finished. */
  private _isTimeElapsed = false
  /** The bottom of the chapter has been on screen. */
  private _isBottomSeen = false
  /** The progress for this page has already been saved. */
  private _isProgressSaved = false

  constructor(doc: DocView, restorePosition: boolean = true) {
    this._doc = doc

    this._tProgress = new Timer(20000, () => {
      this._isTimeElapsed = true
      void this.saveProgressIfPageIsRead()
      void this.addToReadingNowIfNeeded()
    })

    // We use a timer of 0 just to make the function run
    // asynchronously, deferring it to the end of the javascript
    // execution queue.
    this._tPositionRestore = new Timer(0, () => {
      if (restorePosition) {
        this._doc.restoreReadingPosition()
      }
    })

    this._tPositionSave = new Timer(3000, () => {
      if (isDocContentPage()) {
        saveReadingPositionFromScroll(this._doc, this._doc.currentPage)
      }
    })

    // Add scroll listener, it will initiate the reading position save
    this._scrollHandler = debounce(() => {
      this.scrollEvent()
    }, 500)
    emitter.on('appScroll', this._scrollHandler)
  }

  /*
   * Remove event listener and stop timers on destroy
   */
  destroy(): void {
    this.removeScrollEventListener()
    this.clearTimers()
    clearInterval(this._doc.checklistReadTimeStepInterval19055)
  }

  clearTimers(): void {
    this._tProgress.clear()
    this._tPositionRestore.clear()
    this._tPositionSave.clear()

    // These conditions describe the page we are leaving, so they have to go with the timers.
    // Every restart path calls this method, so we wouldn't count conditions from previous chapters/pages.
    this._isTimeElapsed = false
    this._isBottomSeen = false
    this._isProgressSaved = false
  }

  /**
   * Called when the bottom of the chapter has been on screen.
   */
  markBottomSeen(): void {
    this._isBottomSeen = true
    void this.saveProgressIfPageIsRead()
  }

  /**
   * Save the progress once the current page counts as read.
   *
   * A chapter also needs its bottom to have been on screen. The order
   * doesn't matter, the save happens when the second of the two conditions
   * is met.
   */
  async saveProgressIfPageIsRead(): Promise<void> {
    if (this._isProgressSaved || !this._isTimeElapsed || !isDocContentPage()) {
      return
    }

    // Chapters need the bottom to be seen and are only completed once.
    if (
      this._doc.isCurrentPageChapter &&
      (!this._isBottomSeen || this._doc.isCompletedPage(this._doc.currentPage))
    ) {
      return
    }

    this._isProgressSaved = true
    try {
      const completed = await saveProgress(this._doc, this._doc.currentPage)
      if (!completed || this._doc.doc_type !== 'book') {
        return
      }

      // Enroll the user in the guide completion experiment, if needed, and
      // register the popup for treatment users.
      await completionService.registerCompletion(this._doc, 'passive')
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  /**
   * Add the doc to "Reading Now" when the 20 second timer finishes.
   */
  async addToReadingNowIfNeeded(): Promise<void> {
    if (
      !isDocContentPage() ||
      this._doc.user.list ||
      (isFreePlan() && !this._doc.is_free)
    ) {
      return
    }

    try {
      const response = await backend.addToReadingNow(this._doc)
      this._doc.user.list = response.list
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  /**
   * Pause the reading progress timer.
   */
  pauseProgressTimer(): void {
    this._tProgress.pause()
  }

  /**
   * Resume the reading progress timer.
   */
  resumeProgressTimer(): void {
    this._tProgress.resume()
  }

  /*
   * Start (or restart) tracking
   */
  restartTracking(): void {
    // Clear previous timers.
    this.clearTimers()

    // Start Save Progress timer once
    this._tProgress.start()

    // Start Restore reading position timer once.
    // We don't put _doc.restoreReadingPosition() into DocBasePage.loadDocument()
    // because when calling from here, appEl.scrollHeight differs from correct
    // value (when page is fully loaded).
    this._tPositionRestore.start()

    // Start Save Reading Position timer at start
    // and later scroll event (this._scrollHandler listener) will initiates save reading position
    this.startSavePositionTimer()
  }

  /**
   * Restart tracking timers without restoring saved scroll position.
   * Used when navigating to highlights/bookmarks to:
   * - Continue tracking progress
   * - Continue saving scroll position
   * - Skip restoring the previously saved position (to allow highlight scroll)
   */
  restartTrackingWithoutRestore(): void {
    this.clearTimers()
    this._tProgress.start()
    // Intentionally Skip _tPositionRestore.start() to not restore position
    this.startSavePositionTimer()
  }

  startSavePositionTimer(): void {
    // Clear previous - we need only the last.
    this._tPositionSave.clear()
    this._tPositionSave.start()
  }

  /*
   * Scroll event starts timer for reading position saving.
   */
  scrollEvent(): void {
    this.startSavePositionTimer()
  }

  /**
   * Detach the scroll event handler.
   */
  removeScrollEventListener(): void {
    // Remove Event listener
    emitter.off('appScroll', this._scrollHandler)
    // Clear running timer for postiton save.
    this._tPositionSave.clear()
  }
}

/**
 * Save doc reading position using the page scroll amount.
 */
export async function saveReadingPositionFromScroll(
  doc: DocWithContent,
  chapter: DocContent,
): Promise<void> {
  if (!isDocContentPage()) {
    // We can only save the reading position from scroll in content pages.
    return
  }

  const appEl = assertNotNull(document.getElementById('app'))
  const positionPercent =
    // For Safari-based browsers scrollTop can be negative (#2617)
    // Just return 0 in this case
    appEl.scrollTop < 0
      ? 0
      : Math.round((appEl.scrollTop / appEl.scrollHeight) * 100)

  await saveReadingPosition(doc, chapter, positionPercent)
}

/**
 * Save the doc reading position in the given chapter.
 */
export async function saveReadingPosition(
  doc: DocWithContent,
  chapter: DocContent,
  positionPercent: number,
  options: { forceBackendSave?: boolean } = {},
): Promise<void> {
  const positionChanged = !(
    doc.user.position_chapter === chapter.url_slug &&
    Math.round(doc.user.position_percent - positionPercent) === 0
  )
  if (!positionChanged && !options.forceBackendSave) {
    return
  }

  if (positionChanged) {
    doc.user.position_chapter = chapter.url_slug
    doc.user.position_percent = positionPercent

    // Update cached doc data
    // We update cache doc data before making a network call
    // so that the cache is always up-to-date regardless of
    // whether the network call succeeds or fails (user goes offline in the process)
    await updateDocsUserDataCache(doc.id, doc.user)
    await updateUserDocsCache(doc)
  }

  const response = await backend.saveReadingPosition(chapter, positionPercent)

  // Accessed has changed, and `list` might have changed too.
  doc.user.accessed = response.accessed
  doc.user.list = response.list
  await updateDocsUserDataCache(doc.id, doc.user)
}

/**
 * Save doc progress by marking the given chapter as completed.
 *
 * @returns Whether this save moved the doc from below 100% to 100%.
 */
export async function saveProgress(
  doc: DocWithContent,
  chapter: DocContent,
): Promise<boolean> {
  if (isFreePlan() && !doc.is_free) {
    // If the user is on free plan and the doc is not free,
    // don't track the reading progress (because content is
    // not fully avaiable, part is hidden behind the end-of-content block).
    return false
  }

  const response = await backend.saveProgress(chapter)
  // Another save may have completed the guide while this request was pending.
  const newlyCompleted = doc.user.progress < 100 && response.progress === 100
  // Keep the doc in sync with the back end.
  doc.user.progress = response.progress
  doc.user.completed_at = response.completed_at
  // Completing a chapter can change the list too, e.g. a doc marked as not
  // started goes back to "Reading Now".
  doc.user.list = response.list
  await updateDocsUserDataCache(doc.id, doc.user)

  Mixpanel.trackContentCompleted(doc)

  if (doc.user.progress === 100) {
    if (newlyCompleted) {
      // Send the "Doc Completed" event if this is the first time we've
      // reached 100%. See https://github.com/shortformhq/main/issues/16955.
      Mixpanel.trackDocCompleted(doc)
    }

    // The setFirstDoc routine only sets the property if it doesn't exist,
    // so we don't need to check whether this completion is new.
    Mixpanel.setFirstDoc('Completed', doc.title, doc.doc_type)
  }

  // Note: it is better to return content from backend save progress
  // API, so we could just reuse backend logic here and do something
  // like content.completed = response.data.completed
  if (chapter.content_type === 'chapter') {
    // Mark chapters as completed.
    const now = new Date()
    chapter.completed = now.toISOString()

    // Track "First <doc_type> Summary Completed" if this is the first page
    // (ie. the "1-Page Summary" page) and it's not an article.
    if (chapter.order === 0 && doc.doc_type !== 'article') {
      Mixpanel.setFirstDoc('Summary Completed', doc.title, doc.doc_type)
    }
  }

  return newlyCompleted
}
